import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { Platform, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { normalizeSettingsSearch } from './settingsSearchText';

const SearchTargetContext = createContext(null);

export function SettingsSearchTargetProvider({ target, requestKey, scrollRef, contentRef, revealRef, children }) {
  const revealedY = useRef(null);
  const targetNode = useRef(null);
  useEffect(() => { revealedY.current = null; targetNode.current = null; }, [target, requestKey]);
  const reveal = useCallback(node => {
    if (!node || !contentRef.current) return;
    targetNode.current = node;
    node.measureLayout?.(contentRef.current, (_x, y) => {
      if (revealedY.current !== null && Math.abs(revealedY.current - y) < 1) return;
      const firstReveal = revealedY.current === null;
      revealedY.current = y;
      scrollRef.current?.scrollTo({ y: Math.max(0, y - 20), animated: false });
      // Focus the non-interactive container, never a switch or destructive action.
      if (firstReveal && Platform.OS === 'web') node.focus?.({ preventScroll: true });
    }, () => {});
  }, [target, requestKey, scrollRef, contentRef]);
  useEffect(() => {
    if (!revealRef) return;
    revealRef.current = () => reveal(targetNode.current);
    return () => { revealRef.current = null; };
  }, [reveal, revealRef]);
  return <SearchTargetContext.Provider value={{ target: normalizeSettingsSearch(target), requestKey, reveal }}>{children}</SearchTargetContext.Provider>;
}

export default function SettingsSearchTarget({ label, style, onLayout, ...props }) {
  const context = useContext(SearchTargetContext);
  const ref = useRef(null);
  const { colors } = useTheme();
  const selected = !!context?.target && context.target === normalizeSettingsSearch(label);
  useEffect(() => {
    if (!selected) return;
    // Allow both a newly selected panel and asynchronously loaded controls to lay out.
    const timer = setTimeout(() => context.reveal(ref.current), 80);
    return () => clearTimeout(timer);
  }, [selected, context?.requestKey, context?.reveal]);
  return <View {...props} ref={ref} collapsable={selected ? false : undefined}
    {...(selected && Platform.OS === 'web' ? { tabIndex: -1, dataSet: { settingsSearchTarget: 'true' } } : {})}
    onLayout={event => { onLayout?.(event); if (selected) context.reveal(ref.current); }}
    style={[style, selected && { backgroundColor: colors.solid_color_tertiary, borderRadius: 10,
      ...(Platform.OS === 'web' ? { outlineWidth: 2, outlineStyle: 'solid', outlineColor: colors.text_color_secondary, outlineOffset: 3 } : {}) }]} />;
}
